import type { ExecutiveDigestContent } from "~/api/conversationTypes";

/**
 * The Executive Digest, as markdown (W8 §B.7 / S8).
 *
 * kms built this as a 154-line ProseMirror node-tree splice: construct headings and lists as
 * nodes, find the `conversation` node whose `attrs.conversationId` matches, walk forward to
 * the next level-2 heading to delete any previous digest, then insert. That is the heaviest
 * editor coupling in the wave and it exists only because kms had no markdown lane.
 *
 * Slate renders the same structure to MARKDOWN and applies it through the one write path
 * (`documentWrite`). The result is identical text with none of the coupling: it works on a
 * document being edited, on a document being read (through `documents.sculpt`), and it can be
 * shown to a person BEFORE it is written, which a node splice cannot.
 *
 * The fallbacks are kms's, verbatim, and they matter: a digest with an empty "Next Actions"
 * heading reads as "there are none", while `Define next actions` reads as "nobody has said".
 */

export const DIGEST_HEADING = "Executive Digest";

const TRANSLATE_DEFAULTS = {
  heading: DIGEST_HEADING,
  summary: "Executive Summary",
  truths: "Derived Truths",
  decisions: "Decisions",
  risks: "Risks / Unknowns",
  nextActions: "Next Actions",
  references: "References",
  noSummary: "No summary yet",
  none: "None noted.",
  defineNext: "Define next actions",
  lens: "Conversation lens",
  owner: "Owner",
  due: "Due",
  followUp: "Follow up",
  tbd: "TBD",
};

export type DigestWords = typeof TRANSLATE_DEFAULTS;

function section(title: string, lines: string[], fallback: string): string {
  const body = lines.map((line) => `- ${line}`).join("\n");
  return `### ${title}\n\n${body || `- ${fallback}`}`;
}

/**
 * `content` is the structured digest the server already computed and returned on `commit` —
 * this only renders it. Nothing here invents a fact.
 */
export function digestMarkdown(
  content: ExecutiveDigestContent,
  words: Partial<DigestWords> = {},
): string {
  const w = { ...TRANSLATE_DEFAULTS, ...words };

  const nextActions = content.nextActions.map((entry) => {
    const action = entry.action.trim() || w.followUp;
    return `${action} (${w.owner}: ${entry.owner?.trim() || w.tbd}, ${w.due}: ${
      entry.dueDate?.trim() || w.tbd
    })`;
  });

  const references = content.references
    .filter((entry) => entry.label.trim() || entry.url.trim())
    .map((entry) =>
      entry.url.trim()
        ? `[${entry.label.trim() || entry.url.trim()}](${entry.url.trim()})`
        : entry.label,
    );

  const blocks = [
    `## ${w.heading}`,
    section(w.summary, content.executiveSummary, w.noSummary),
    // Not one of kms's five headings, and deliberately added: `edContent.derivedTruths` is
    // populated by the server and kms rendered it NOWHERE, so committing threw it away. It is
    // omitted entirely when empty, so a digest without truths looks exactly as it did.
    ...(content.derivedTruths.length > 0 ? [section(w.truths, content.derivedTruths, w.none)] : []),
    section(w.decisions, content.decisions, w.none),
    section(w.risks, content.risks, w.none),
    section(w.nextActions, nextActions, w.defineNext),
    section(w.references, references, w.lens),
  ];

  return blocks.join("\n\n");
}

/**
 * Replace a previous digest in a markdown body, or append a first one.
 *
 * Same rule as kms's node walk, expressed on text: the section runs from its own `## Executive
 * Digest` heading to the next level-2 heading (or the end). Committing twice must not leave
 * two digests, and must not eat the section that follows.
 */
export function replaceDigest(body: string, digest: string, heading = DIGEST_HEADING): string {
  const lines = body.split("\n");
  const start = lines.findIndex((line) => line.trim() === `## ${heading}`);
  if (start < 0) {
    return `${body.trimEnd()}\n\n${digest}\n`.trimStart();
  }
  let end = lines.length;
  for (let index = start + 1; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    if (/^##(?!#)\s/.test(line)) {
      end = index;
      break;
    }
  }
  const before = lines.slice(0, start).join("\n").trimEnd();
  const after = lines.slice(end).join("\n").trimStart();
  return [before, digest, after].filter((part) => part.length > 0).join("\n\n");
}
